import React, { useState, useEffect, useRef } from "react";
import styles from "../styles/MerchantOnepager.module.css";
import Header from "../components/Header";
import Footer from "../components/Footer";
import brand from "../assets/brand.svg";
import { useNavigate, useLocation } from "react-router-dom";
import {
  FiUser,
  FiBriefcase,
  FiDollarSign,
  FiPieChart,
  FiArrowRight,
  FiArrowLeft,
  FiCheck,
} from "react-icons/fi";

const STEPS = [
  { id: 1, label: "Personal" },
  { id: 2, label: "Company" },
  { id: 3, label: "Business" },
  { id: 4, label: "Market" },
];

const PRODUCTS = [
  "Merchant App",
  "QR",
  "Payment Gateway",
  "Parking",
  "E-Commerce Integration",
  "Hospital Management",
];

const TURNOVER = [
  "Less than 1M",
  "1M-50M",
  "50M-100M",
  "100M-500M",
  "500M-1B",
  "More than 1B",
];

const SEGMENTS = [
  { value: "Healthcar", label: "Healthcare" },
  { value: "add_value", label: "Hospitality" },
  { value: "integrate", label: "Financial Services" },
  { value: "professoinal", label: "Professional Services" },
  { value: "Education", label: "Education" },
  { value: "Mobility", label: "Mobility (Delivery)" },
  { value: "Humanitarian", label: "Humanitarian" },
  { value: "Commerce", label: "e-Commerce" },
  { value: "Gaming", label: "Gaming" },
  { value: "provide_services", label: "Logistics and Distribution" },
  { value: "embed", label: "Travel" },
  { value: "Retail", label: "Retail" },
  { value: "campaign", label: "Government Services" },
  { value: "introduce", label: "Development Partners" },
  { value: "fmcg", label: "FMCG Distribution" },
  { value: "repair", label: "All" },
  { value: "other", label: "Other" },
];

function Field({ id, label, error, hint, wide, children }) {
  return (
    <div className={`${styles.field} ${wide ? styles.fieldWide : ""}`}>
      <label htmlFor={id}>{label}</label>
      {children}
      {hint ? <p className={styles.hint}>{hint}</p> : null}
      {error ? <span className={styles.errorText}>{error}</span> : null}
    </div>
  );
}

function Choice({ name, value, checked, onChange, children }) {
  return (
    <label className={`${styles.choice} ${checked ? styles.choiceOn : ""}`}>
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        onChange={onChange}
      />
      <span className={styles.choiceMark} aria-hidden="true" />
      <span className={styles.choiceText}>{children}</span>
    </label>
  );
}

const MerchantOnboarding = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const formRef = useRef(null);
  const previousStep = useRef(1);

  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  const [companyName, setCompanyName] = useState("");
  const [role, setRole] = useState("");
  const [website, setWebsite] = useState("");
  const [country, setCountry] = useState("");

  const [hasLicense, setHasLicense] = useState(null);
  const [licenseNumber, setLicenseNumber] = useState("");
  const [productInterest, setProductInterest] = useState(null);
  const [turnover, setTurnover] = useState(null);
  const [businessNeeds, setBusinessNeeds] = useState("");

  const [customInterest, setCustomInterest] = useState("");
  const [selectedInterest, setSelectedInterest] = useState(null);

  const [errors, setErrors] = useState({});
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const previousRestoration = history.scrollRestoration;
    history.scrollRestoration = "manual";
    window.scrollTo(0, 0);
    return () => {
      history.scrollRestoration = previousRestoration;
    };
  }, [location]);

  useEffect(() => {
    if (previousStep.current === currentStep) return;
    previousStep.current = currentStep;
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [currentStep]);

  const validateStep1 = () => {
    const newErrors = {};
    let isValid = true;

    if (!firstName.trim()) {
      newErrors.firstName = "First name is required.";
      isValid = false;
    }

    if (!lastName.trim()) {
      newErrors.lastName = "Last name is required.";
      isValid = false;
    }

    if (!email.trim()) {
      newErrors.email = "Email address is required.";
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = "Email address is invalid.";
      isValid = false;
    }

    if (!phone.trim()) {
      newErrors.phone = "Phone number is required.";
      isValid = false;
    } else if (!/^\d{10,}$/.test(phone)) {
      newErrors.phone = "Phone number must be at least 10 digits.";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const validateStep2 = () => {
    const newErrors = {};
    let isValid = true;

    if (!companyName.trim()) {
      newErrors.companyName = "Company name is required.";
      isValid = false;
    }

    if (!role.trim()) {
      newErrors.role = "Role is required.";
      isValid = false;
    }

    if (!website.trim()) {
      newErrors.website = "Website is required.";
      isValid = false;
    } else if (!/^https?:\/\/.+/.test(website)) {
      newErrors.website = "Website must start with http:// or https://";
      isValid = false;
    }

    if (!country.trim()) {
      newErrors.country = "Country is required.";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const validateStep3 = () => {
    if (
      hasLicense === null ||
      productInterest === null ||
      turnover === null ||
      (hasLicense === "yes" &&
        (licenseNumber.trim() === "" || isNaN(licenseNumber)))
    ) {
      setErrorMessage(
        hasLicense === "yes" && isNaN(licenseNumber)
          ? "License number must be a number."
          : "Please fill all required fields."
      );
      return false;
    }
    return true;
  };

  const validateStep4 = () => {
    if (!selectedInterest) {
      setErrorMessage("Please select your market segment.");
      return false;
    }

    if (selectedInterest === "other" && !customInterest.trim()) {
      setErrorMessage("Please specify your market segment.");
      return false;
    }
    return true;
  };

  const handleNext = () => {
    setErrorMessage("");
    if (currentStep === 1 && !validateStep1()) return;
    if (currentStep === 2 && !validateStep2()) return;
    if (currentStep === 3 && !validateStep3()) return;
    if (currentStep === 4) {
      handleSubmit();
      return;
    }
    setErrors({});
    setCurrentStep(currentStep + 1);
  };

  const handlePrevious = () => {
    if (currentStep === 1) return;
    setErrorMessage("");
    setErrors({});
    setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep4()) return;

    const marketSegment =
      selectedInterest === "other" ? customInterest.trim() : selectedInterest;

    const formData = {
      partner_id: firstName + " " + lastName,
      email_id: email,
      phone_number: phone,
      address: country,
      company_name: companyName,
      website: website,
      business_licence_no: hasLicense === "yes" ? licenseNumber : "",
      product_types: productInterest,
      market_segment: marketSegment,
      partnership_type: "",
      merchant_type: "",
    };

    console.log("Form Data to be submitted:", {
      "Personal Information": {
        "First Name": firstName,
        "Last Name": lastName,
        Email: email,
        Phone: phone,
      },
      "Company Information": {
        "Company Name": companyName,
        Role: role,
        Website: website,
        Country: country,
      },
      "Business Details": {
        "Has License": hasLicense,
        "License Number": hasLicense === "yes" ? licenseNumber : "N/A",
        "Product Interest": productInterest,
        "Annual Turnover": turnover,
        "Business Needs": businessNeeds,
      },
      "Market Segment": {
        "Selected Segment": selectedInterest,
        "Custom Segment": selectedInterest === "other" ? customInterest : "N/A",
        "Final Segment Value": marketSegment,
      },
    });

    setLoading(true);
    try {
      console.log(formData);
      console.log(JSON.stringify(formData));
      const response = await fetch(
        "https://inhouse.arifpay.org/api/merchant/create",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        }
      );

      const data = await response.json();
      console.log("data" + JSON.stringify(data));
      if (response.ok) {
        setShowConfirmation(true);
        setErrorMessage("");
      } else {
        setErrorMessage(data.message || "Submission failed.");
      }
    } catch (error) {
      setErrorMessage("An error occurred. Please check your connection.");
    } finally {
      setLoading(false);
    }
  };

  const handleDoneClick = () => {
    setShowConfirmation(false);
    navigate("/");
  };

  const inputClass = (name) => (errors[name] ? styles.inputError : "");

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className={styles.formSection}>
            <div className={styles.stepHead}>
              <span className={styles.stepIcon}>
                <FiUser />
              </span>
              <div>
                <p className={styles.kicker}>Step 1 of 4</p>
                <h2 className={styles.sectionTitle}>Personal information</h2>
                <p className={styles.sectionText}>
                  Tell us how to reach the person applying.
                </p>
              </div>
            </div>
            <div className={styles.grid}>
              <Field id="firstName" label="First name" error={errors.firstName}>
                <input
                  type="text"
                  id="firstName"
                  placeholder="John"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className={inputClass("firstName")}
                  autoComplete="given-name"
                />
              </Field>
              <Field id="lastName" label="Last name" error={errors.lastName}>
                <input
                  type="text"
                  id="lastName"
                  placeholder="Doe"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className={inputClass("lastName")}
                  autoComplete="family-name"
                />
              </Field>
              <Field id="email" label="Email address" error={errors.email} wide>
                <input
                  type="email"
                  id="email"
                  placeholder="example@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={inputClass("email")}
                  autoComplete="email"
                />
              </Field>
              <Field
                id="phone"
                label="Phone number"
                error={errors.phone}
                hint="Digits only, at least 10."
                wide
              >
                <input
                  type="tel"
                  id="phone"
                  placeholder="0912345678"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className={inputClass("phone")}
                  autoComplete="tel"
                  inputMode="numeric"
                />
              </Field>
            </div>
          </div>
        );
      case 2:
        return (
          <div className={styles.formSection}>
            <div className={styles.stepHead}>
              <span className={styles.stepIcon}>
                <FiBriefcase />
              </span>
              <div>
                <p className={styles.kicker}>Step 2 of 4</p>
                <h2 className={styles.sectionTitle}>Company information</h2>
                <p className={styles.sectionText}>
                  Share the business you want to onboard.
                </p>
              </div>
            </div>
            <div className={styles.stack}>
              <Field
                id="companyName"
                label="Company name"
                error={errors.companyName}
              >
                <input
                  type="text"
                  id="companyName"
                  placeholder="John Doe Ltd"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  className={inputClass("companyName")}
                  autoComplete="organization"
                />
              </Field>
              <Field id="role" label="Your role at the company" error={errors.role}>
                <input
                  type="text"
                  id="role"
                  placeholder="Manager"
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  className={inputClass("role")}
                />
              </Field>
              <Field
                id="website"
                label="Website"
                error={errors.website}
                hint="Start with https:// or http://"
              >
                <input
                  type="url"
                  id="website"
                  placeholder="https://www.example.com"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  className={inputClass("website")}
                />
              </Field>
              <Field id="country" label="Country" error={errors.country}>
                <input
                  type="text"
                  id="country"
                  placeholder="Ethiopia"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  className={inputClass("country")}
                  autoComplete="country-name"
                />
              </Field>
            </div>
          </div>
        );
      case 3:
        return (
          <div className={styles.formSection}>
            <div className={styles.stepHead}>
              <span className={styles.stepIcon}>
                <FiDollarSign />
              </span>
              <div>
                <p className={styles.kicker}>Step 3 of 4</p>
                <h2 className={styles.sectionTitle}>Business details</h2>
                <p className={styles.sectionText}>
                  Help us match the right products to your business.
                </p>
              </div>
            </div>

            <div className={styles.group}>
              <p className={styles.groupLabel}>Do you have a business license?</p>
              <div className={styles.pair}>
                <Choice
                  name="license"
                  value="yes"
                  checked={hasLicense === "yes"}
                  onChange={() => setHasLicense("yes")}
                >
                  Yes
                </Choice>
                <Choice
                  name="license"
                  value="no"
                  checked={hasLicense === "no"}
                  onChange={() => setHasLicense("no")}
                >
                  No
                </Choice>
              </div>
            </div>

            {hasLicense === "yes" && (
              <div className={styles.group}>
                <Field id="licenseNumber" label="Business license number">
                  <input
                    type="text"
                    id="licenseNumber"
                    placeholder="Enter license number"
                    value={licenseNumber}
                    onChange={(e) => setLicenseNumber(e.target.value)}
                    inputMode="numeric"
                  />
                </Field>
              </div>
            )}

            <div className={styles.group}>
              <p className={styles.groupLabel}>
                What are you primarily interested in?
              </p>
              <div className={styles.choiceGrid}>
                {PRODUCTS.map((item) => (
                  <Choice
                    key={item}
                    name="product"
                    value={item}
                    checked={productInterest === item}
                    onChange={() => setProductInterest(item)}
                  >
                    {item}
                  </Choice>
                ))}
              </div>
            </div>

            <div className={styles.group}>
              <p className={styles.groupLabel}>Annual turnover (ETB)</p>
              <div className={`${styles.choiceGrid} ${styles.choiceGridTight}`}>
                {TURNOVER.map((amount) => (
                  <Choice
                    key={amount}
                    name="turnover"
                    value={amount}
                    checked={turnover === amount}
                    onChange={() => setTurnover(amount)}
                  >
                    {amount}
                  </Choice>
                ))}
              </div>
            </div>

            <div className={styles.group}>
              <Field
                id="businessNeeds"
                label="Tell us more about your business needs and timeline"
              >
                <textarea
                  id="businessNeeds"
                  placeholder="What do you want to launch, and when?"
                  value={businessNeeds}
                  onChange={(e) => setBusinessNeeds(e.target.value)}
                />
              </Field>
            </div>
          </div>
        );
      case 4:
        return (
          <div className={styles.formSection}>
            <div className={styles.stepHead}>
              <span className={styles.stepIcon}>
                <FiPieChart />
              </span>
              <div>
                <p className={styles.kicker}>Step 4 of 4</p>
                <h2 className={styles.sectionTitle}>Market segment</h2>
                <p className={styles.sectionText}>
                  Choose the industry that best describes your business.
                </p>
              </div>
            </div>
            <div className={styles.choiceGrid}>
              {SEGMENTS.map(({ value, label }) => (
                <Choice
                  key={value}
                  name="interest"
                  value={value}
                  checked={selectedInterest === value}
                  onChange={() => setSelectedInterest(value)}
                >
                  {label}
                </Choice>
              ))}
            </div>
            {selectedInterest === "other" && (
              <div className={styles.group}>
                <Field id="customInterest" label="Please specify">
                  <input
                    type="text"
                    id="customInterest"
                    value={customInterest}
                    onChange={(e) => setCustomInterest(e.target.value)}
                    placeholder="Enter your market segment"
                  />
                </Field>
              </div>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className={styles.page}>
      <Header />

      <div className={styles.shell}>
        <div className={styles.layout}>
          <aside className={styles.aside}>
            <div className={styles.brandRow}>
              <img src={brand} alt="" className={styles.logo} />
              <p className={styles.badge}>Merchant onboarding</p>
            </div>
            <h1 className={styles.title}>Grow your business with us</h1>
            <p className={styles.lead}>
              Join our network of merchants and unlock new opportunities.
              Complete these four steps and our team will review your
              application.
            </p>
            <ul className={styles.checklist}>
              {STEPS.map((step) => (
                <li
                  key={step.id}
                  className={currentStep > step.id ? styles.done : ""}
                >
                  <FiCheck />
                  {step.label}
                  {currentStep === step.id ? " · in progress" : ""}
                </li>
              ))}
            </ul>
          </aside>

          <main className={styles.card} id="onboarding-form" ref={formRef}>
            <ol className={styles.stepper}>
              {STEPS.map((step) => (
                <li
                  key={step.id}
                  className={`${styles.step} ${
                    currentStep === step.id
                      ? styles.active
                      : currentStep > step.id
                      ? styles.completed
                      : ""
                  }`}
                  aria-current={currentStep === step.id ? "step" : undefined}
                >
                  <span className={styles.stepNumber}>
                    {currentStep > step.id ? <FiCheck /> : step.id}
                  </span>
                  <span className={styles.stepLabel}>{step.label}</span>
                </li>
              ))}
            </ol>
            <div className={styles.track} aria-hidden="true">
              <div
                className={styles.trackFill}
                style={{ width: `${(currentStep / STEPS.length) * 100}%` }}
              />
            </div>

            {renderStep()}

            {errorMessage ? (
              <p className={styles.formError} role="alert">
                {errorMessage}
              </p>
            ) : null}

            <div className={styles.actions}>
              {currentStep > 1 ? (
                <button
                  type="button"
                  className={styles.buttonSecondary}
                  onClick={handlePrevious}
                >
                  <FiArrowLeft /> Previous
                </button>
              ) : (
                <span />
              )}
              <button
                type="button"
                className={styles.buttonPrimary}
                onClick={handleNext}
                disabled={loading}
              >
                {loading ? (
                  "Processing..."
                ) : currentStep === 4 ? (
                  <>
                    Submit <FiCheck />
                  </>
                ) : (
                  <>
                    Next <FiArrowRight />
                  </>
                )}
              </button>
            </div>
          </main>
        </div>
      </div>

      {showConfirmation && (
        <div className={styles.overlay} role="dialog" aria-modal="true">
          <div className={styles.modal}>
            <div className={styles.modalIcon}>
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path
                  fill="currentColor"
                  d="M12 2C6.5 2 2 6.5 2 12S6.5 22 12 22 22 17.5 22 12 17.5 2 12 2M10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z"
                />
              </svg>
            </div>
            <h3>Application submitted</h3>
            <p>
              Thank you for your application. Our team will review your
              information and contact you within 2 business days.
            </p>
            <button type="button" onClick={handleDoneClick}>
              Back to home
            </button>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default MerchantOnboarding;
